import type { Command, Event, PublicState } from '@/api/generated/game';
import type { ErrorCode, RoomMode } from '@/api/generated/ws-protocol';

/**
 * Intent — намерение игрока. Это ровно game.Command из контракта: клиент шлёт
 * только намерения, решение принимает сервер. Собирать их руками не надо —
 * есть intents.* из ./intents.
 */
export type Intent = Command;

/**
 * ConnectionStatus — что сейчас с соединением.
 *
 * `closed` и `fatal` разведены намеренно: из первого выход — новая партия,
 * из второго — обновление приложения или повторный вход. Молча ретраить ни то,
 * ни другое нельзя.
 */
export type ConnectionStatus =
  | 'idle' // ещё не подключались или отключились сами
  | 'connecting' // первая попытка
  | 'online' // хендшейк прошёл, состояние актуально
  | 'reconnecting' // оборвалось, ждём следующей попытки
  | 'closed' // сервер закрыл комнату
  | 'fatal'; // сами не восстановимся: версия контракта или мёртвая сессия

/**
 * TransportEvent — всё, что транспорт рассказывает приложению.
 *
 * Одной подписки хватает на всё: подписка только на состояние потеряла бы
 * места этого соединения (seats), отказ на конкретную кнопку (replyTo) и сам
 * факт обрыва — а без него UI не имеет права показывать кнопки.
 */
export type TransportEvent =
  | { kind: 'status'; status: ConnectionStatus; attempt: number }
  | {
      kind: 'hello';
      roomCode: string;
      roomMode: RoomMode;
      seats: number[];
      state: PublicState;
    }
  | { kind: 'state'; state: PublicState }
  | { kind: 'events'; events: Event[] }
  | { kind: 'error'; code: ErrorCode; message: string; replyTo: string | undefined }
  | { kind: 'closed'; reason: string };

export interface ConnectOpts {
  /** wss://host/ws */
  url: string;
  roomCode: string;
  /**
   * Токен запрашивается на каждую попытку, а не передаётся значением: сессия
   * живёт 7 дней, а переподключений за партию бывает десятки. `refresh` просят
   * после отказа `unauthorized` — тогда сессию надо выпустить заново.
   */
  getToken(opts: { refresh: boolean }): Promise<string>;
}

/**
 * GameTransport — единственная дверь к серверу партии.
 *
 * Реализация одна, WsTransport. Интерфейс нужен затем, чтобы в тестах экранов
 * подставлять FakeTransport и не поднимать сокет ради проверки разметки.
 * Прямых обращений к WebSocket вне WsTransport быть не должно.
 */
export interface GameTransport {
  /** Резолвится на первом hello_ok, реджектится на fatal. Дальше держит соединение сам. */
  connect(opts: ConnectOpts): Promise<void>;
  /**
   * Отправляет намерение и возвращает id конверта — по нему error.replyTo
   * связывается с нажатой кнопкой.
   *
   * Бросает TransportOfflineError, если соединения нет. Копить намерения и
   * досылать пачкой запрещено: за время обрыва ход мог уйти дальше.
   */
  send(intent: Intent): string;
  /**
   * Выйти из комнаты. Это не намерение партии, а сообщение протокола (`leave`):
   * в лобби оно освобождает место, у хоста закрывает комнату целиком.
   * Бросает TransportOfflineError по той же причине, что и `send`.
   */
  leave(): void;
  subscribe(fn: (event: TransportEvent) => void): () => void;
  disconnect(): void;
}

export class TransportOfflineError extends Error {
  constructor() {
    super('нет соединения с партией');
    this.name = 'TransportOfflineError';
  }
}
